import React, { createRef, forwardRef, memo } from 'react'

import PhoneInput, { Country, Props as PhoneInputProps } from 'react-phone-number-input'
import { getCountryCallingCode } from 'react-phone-number-input/input'

import 'react-phone-number-input/style.css'

import { useTranslation } from 'react-i18next'

import { OECDCountries } from '@/models/countries'

import { Input, StyledPhoneInput } from './atoms'

const getCountriesList = (countryEnum: object) => Object.keys(countryEnum) as Array<Country>

interface OwnProps {
  hasError?: boolean
  countryEnum?: object
}

type CustomInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>

const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>((props, ref) => <Input ref={ref} {...props} type="tel" />)
CustomInput.displayName = 'CustomInput'

const PhoneNumberInput = <D extends object>({ hasError, countryEnum, onChange, ...rest }: OwnProps & PhoneInputProps<D>) => {
  const { t } = useTranslation()
  const ref = createRef<null>()

  const getCountryLabels = (): { [key: string]: string } => {
    let labels: { [key: string]: string } = {}
    Object.keys(countryEnum ?? OECDCountries).forEach((key: string) => {
      labels[key] = `${t(`country.${key}_name`)} +${getCountryCallingCode(key as Country)}`
    })

    // Add default select label to country list
    labels = { ...labels, ZZ: t('country.ZZ_name') }

    return labels
  }

  return (
    <StyledPhoneInput>
      <PhoneInput
        ref={ref}
        international
        countrySelectProps={{ unicodeFlags: true }}
        inputComponent={CustomInput}
        onChange={onChange}
        countries={getCountriesList(countryEnum ?? OECDCountries)}
        labels={getCountryLabels()}
        autoComplete="off"
        {...rest}
      />
    </StyledPhoneInput>
  )
}

PhoneNumberInput.displayName = 'PhoneNumberInput'

export default memo(PhoneNumberInput)
